/**
 * ProjectDetail Component
 * AIDLC Administrative Backend
 *
 * Right panel with 5-tab navigation:
 *   1. Overview -- project summary, milestones, team, health
 *   2. Sprint Board -- Kanban board for the active sprint
 *   3. Backlog -- filtered backlog with sprint assignment
 *   4. Velocity -- burndown and velocity charts
 *   5. Standup -- daily standup view per agent
 */

import { useState, useEffect } from 'react';
import { OverviewTab } from './tabs/OverviewTab';
import { SprintBoardTab } from './tabs/SprintBoardTab';
import { ProjectBacklogTab } from './tabs/ProjectBacklogTab';
import { VelocityTab } from './tabs/VelocityTab';
import { StandupTab } from './tabs/StandupTab';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { getProjectById } from '../../services/projectService';
import type { Project } from '../../types/project';

interface ProjectDetailProps {
  projectId: string;
}

type TabKey = 'overview' | 'sprint' | 'backlog' | 'velocity' | 'standup';

interface TabDef {
  key: TabKey;
  label: string;
}

const TABS: TabDef[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'sprint', label: 'Sprint Board' },
  { key: 'backlog', label: 'Backlog' },
  { key: 'velocity', label: 'Velocity' },
  { key: 'standup', label: 'Standup' },
];

export function ProjectDetail({ projectId }: ProjectDetailProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getProjectById(projectId);
        if (!cancelled) setProject(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    // Reset tab when project changes
    setActiveTab('overview');
    return () => { cancelled = true; };
  }, [projectId]);

  if (isLoading) {
    return <LoadingSpinner message="Loading project details..." />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        Project not found.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 px-6">
        <nav className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-ldaja-blue text-ldaja-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <OverviewTab project={project} />
        )}
        {activeTab === 'sprint' && (
          <SprintBoardTab projectId={project.id} />
        )}
        {activeTab === 'backlog' && (
          <ProjectBacklogTab projectId={project.id} />
        )}
        {activeTab === 'velocity' && (
          <VelocityTab projectId={project.id} />
        )}
        {activeTab === 'standup' && (
          <StandupTab projectId={project.id} assignedAgents={project.assignedAgents} />
        )}
      </div>
    </div>
  );
}
